import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useState } from "react";
import type { TopOccupation } from "~/lib/salary-seo-queries";
import { formatSalary, generateHreflangLinks } from "~/lib/seo-utils";
import { serializeForServer } from "~/lib/server-serialization";

const fetchTopOccupations = createServerFn({ method: "GET" }).handler(
  async () => {
    const { getTopOccupations } = await import("~/lib/salary-seo-queries");
    return serializeForServer(await getTopOccupations(100));
  },
);

export const Route = createFileRoute("/gehalt/")({
  loader: () => fetchTopOccupations(),
  head: () => ({
    meta: [
      { title: "Gehälter in Deutschland (2026) — Gehaltsvergleich nach Beruf | Alchema" },
      {
        name: "description",
        content:
          "Vergleiche Gehälter nach Beruf und Bundesland. Aktuelle Gehaltsdaten der Bundesagentur für Arbeit für über 100 Berufe in Deutschland.",
      },
      { property: "og:title", content: "Gehälter in Deutschland (2026) — Gehaltsvergleich nach Beruf | Alchema" },
      { property: "og:description", content: "Vergleiche Gehälter nach Beruf und Bundesland. Aktuelle Gehaltsdaten der Bundesagentur für Arbeit." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://alchema.eu/gehalt" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Gehälter in Deutschland (2026) | Alchema" },
      { name: "twitter:description", content: "Vergleiche Gehälter nach Beruf und Bundesland." },
    ],
    links: generateHreflangLinks("/gehalt"),
  }),
  component: GehaltIndexPage,
});

function GehaltIndexPage() {
  const occupations = Route.useLoaderData();
  const [search, setSearch] = useState("");

  const filtered = search
    ? occupations.filter((o: TopOccupation) =>
        o.occupationName.toLowerCase().includes(search.toLowerCase()),
      )
    : occupations;

  // Group by ISCO major group (first digit of occupationCode)
  const grouped = new Map<string, typeof occupations>();
  for (const occ of filtered) {
    const group = occ.occupationCode.charAt(0);
    if (!grouped.has(group)) grouped.set(group, []);
    grouped.get(group)!.push(occ);
  }

  const ISCO_LABELS: Record<string, string> = {
    "0": "Militär",
    "1": "Führungskräfte",
    "2": "Akademische Berufe",
    "3": "Techniker & gleichrangige Berufe",
    "4": "Bürokräfte",
    "5": "Dienstleistungsberufe",
    "6": "Land- & Forstwirtschaft",
    "7": "Handwerksberufe",
    "8": "Anlagen- & Maschinenbediener",
    "9": "Hilfsarbeitskräfte",
  };

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <section className="mx-auto max-w-4xl px-6 pb-24 pt-20">
        <h1
          className="text-3xl font-bold tracking-tight"
          style={{ color: "var(--text)" }}
        >
          Gehälter in Deutschland
        </h1>
        <p
          className="mt-3 max-w-xl text-base leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          Aktuelle Gehaltsdaten der Bundesagentur für Arbeit. Vergleiche
          Brutto-Monatsgehälter nach Beruf und Bundesland.
        </p>

        {/* Search */}
        <div className="mt-8">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Beruf suchen..."
            className="w-full max-w-md rounded-[10px] border px-4 py-2.5 text-sm outline-none"
            style={{
              borderColor: "var(--border)",
              background: "var(--surface)",
              color: "var(--text)",
            }}
          />
        </div>

        {/* Occupations by group */}
        {occupations.length === 0 ? (
          <p className="mt-12 text-sm" style={{ color: "var(--text-muted)" }}>
            Keine Gehaltsdaten verfügbar. Daten werden bald geladen.
          </p>
        ) : (
          <div className="mt-8 space-y-10">
            {[...grouped.entries()].map(([group, occs]) => (
              <div key={group}>
                <h2
                  className="text-lg font-semibold"
                  style={{ color: "var(--text)" }}
                >
                  {ISCO_LABELS[group] ?? `Gruppe ${group}`}
                </h2>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {occs.map((occ: TopOccupation) => (
                    <Link
                      key={occ.slug}
                      to="/gehalt/$beruf"
                      params={{ beruf: occ.slug }}
                      className="flex items-center justify-between rounded-xl border px-4 py-3 transition hover:shadow-sm"
                      style={{
                        borderColor: "var(--border)",
                        background: "var(--surface)",
                      }}
                    >
                      <span
                        className="text-sm font-medium"
                        style={{ color: "var(--text)" }}
                      >
                        {occ.occupationName}
                      </span>
                      <span
                        className="text-sm font-semibold"
                        style={{ color: "var(--accent)" }}
                      >
                        {formatSalary(occ.medianMonthlyGross)}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Attribution */}
        <p
          className="mt-16 text-xs"
          style={{ color: "var(--text-muted)" }}
        >
          Quelle: Bundesagentur für Arbeit — Entgeltatlas. Alle Angaben in Euro
          brutto pro Monat.
        </p>
      </section>
    </div>
  );
}
